/**
 * The thumbnail box both hover previews float above the scrubber — the rail's
 * scrub preview and a note marker's — so the two can never drift apart in
 * size, border or edge clamping.
 */
import type { CSSProperties } from 'react';

import type { Intrinsic } from './useMediaController';

export interface PreviewSize {
  width: number;
  height: number;
}

/** CSS size of the thumbnail; a canvas backing store is drawn at 2× for retina. */
const PREVIEW_PORTRAIT: PreviewSize = { width: 76, height: 135 };
const PREVIEW_LANDSCAPE: PreviewSize = { width: 120, height: 68 };
const HALF = 2;
/** A pointer-following box must not hang off either end of the rail. */
const PREVIEW_EDGE_INSET_PX = 42;

export const PREVIEW_BOX_CLASS = 'block rounded-[7px] border border-white/[.34] bg-black';

/** A hovered note's text under its thumbnail: who left it and what it says. */
export interface PreviewNote {
  author: string;
  snippet: string;
}

/** The note box is wider than the thumbnail, so it sets how far from the rail's ends a note's preview may sit. */
const PREVIEW_NOTE_MAX_WIDTH_PX = 220;
export const PREVIEW_NOTE_STYLE: CSSProperties = { maxWidth: `${PREVIEW_NOTE_MAX_WIDTH_PX}px` };
export const PREVIEW_NOTE_CLASS = 'w-max rounded-[9px] bg-zinc-900 px-2.5 py-[7px] text-left text-[11.5px] leading-[1.4] text-white';

/** Before metadata the shape is unknown; the design is portrait-first. */
export function isPortrait(intrinsic: Intrinsic | null): boolean {
  if (!intrinsic) return true;
  return intrinsic.height >= intrinsic.width;
}

export function previewSize(portrait: boolean): PreviewSize {
  return portrait ? PREVIEW_PORTRAIT : PREVIEW_LANDSCAPE;
}

export function previewBoxStyle({ width, height }: PreviewSize): CSSProperties {
  return { width: `${width}px`, height: `${height}px` };
}

/** Half the box, so a wide landscape thumbnail stays inside the rail too. */
export function previewEdgeInsetPx(portrait: boolean): number {
  return Math.max(PREVIEW_EDGE_INSET_PX, previewSize(portrait).width / HALF);
}

/** A note's preview carries its text box too, which must stay inside the rail as well. */
export function noteEdgeInsetPx(portrait: boolean): number {
  return Math.max(previewEdgeInsetPx(portrait), PREVIEW_NOTE_MAX_WIDTH_PX / HALF);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Where a box `insetPx` from each end lands for a point `rawPx` along a rail `widthPx` wide. */
export function clampToRail(rawPx: number, widthPx: number, insetPx: number): number {
  const farEdge = Math.max(insetPx, widthPx - insetPx);
  return clamp(rawPx, insetPx, farEdge);
}
